import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import {
  ModalController,
  PopoverController,
  PopoverOptions,
  IonContent,
  ActionSheetController,
  ViewWillEnter,
} from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import {
  jwtDecode,
  PanAppState,
  PlatformService,
  SelectedCustomerState,
  Tracking,
} from '@panamax/app-state';
import {
  DeliveryStoreService,
  OrderHeader,
  OrderItem,
  OrderItemStoreService,
  OrderStatus,
  MyOrdersStoreService,
  UqeProductNumber,
} from '@usf/ngrx-order';
import { NextDeliveryDetailState } from '@usf/ngrx-order';
import { SelectPricingService } from '@usf/ngrx-pricing';
import { SellerPriceDetails } from '@usf/price-types';
import {
  BehaviorSubject,
  combineLatest,
  Observable,
  of,
  Subscription,
} from 'rxjs';
import { Crumb } from 'src/app/shared/components/breadcrumbs/breadcrumbs.component';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  take,
  tap,
} from 'rxjs/operators';
import { HomeService } from '../home/services/home.service';
import { HandPricingPopoverComponent } from '../pricing/components/hand-pricing-popover/hand-pricing-popover.component';
import { HandPricingCPRandNGPComponent } from '../pricing/components/hand-pricing-cpr-ngp-modal/hand-pricing-cpr-ngp-modal.component';
import { ModalWithNavComponent } from '../shared/components/modal-with-nav/modal-with-nav.component';
import { WarningPopoverComponent } from '../shared/components/popovers/warning-popover/warning-popover.component';
import { PageTitle } from '../shared/constants/page-titles.enum';
import { PATHS } from '../shared/constants/paths';
import { FEATURES } from '../shared/constants/splitio-features';
import { NavigationHelperService } from '../shared/helpers/navigation.helpers.service';
import { PopoverHelperService } from '../shared/helpers/popover.helpers';
import { ModalService } from '../shared/services/modal/modal.service';
import { OrderItemService } from '../shared/services/order-item.service';
import {
  CartItem,
  HandPricingProductDetails,
  OrderViewModel,
  ShipmentTypeEnum,
  TopSellersViewModel,
} from './models/order-view-model';
import { PoPopoverComponent } from './components/purchase-order/po-popover-desktop/po-popover.component';
import { OrderService } from './services/order.service';
import { SubmitModalMobileComponent } from './components/submit-modal-mobile/submit-modal-mobile.component';
import { SimpleActionModalComponent } from '../shared/components/simple-action-modal/simple-action-modal.component';
import { HelpModalDesktopComponent } from '../shared/components/help-modal/help-modal-desktop/help-modal-desktop.component';
import { HelpModalTouchComponent } from '../shared/components/help-modal/help-modal-touch/help-modal-touch.component';
import { OrderTracingService } from './tracking/tracing/order-tracing.service';
import { OrderAnalyticsService } from './tracking/analytics/order-analytics.service';
import { OrderProductCardService } from '../shared/services/product-card/order-product-card.service';
import {
  EllipsisClickData,
  ProductClickData,
} from '../shared/models/usf-product-card.model';
import { ProductsLoadedService } from '@app/did-you-forget/tracking/shared/services/products-loaded.service';
import { WarningPopoverWithTitleComponent } from '@shared/components/popovers/warning-popover/warning-popover-with-title/warning-popover-with-title.component';
import { SearchService } from '@search/services/search.service';
import { OrderMinimumViewModel } from './models/order-minimum-view-model';
import moment from 'moment';
import { SharedTrackingService } from '@shared/services/analytics/shared-tracking.service';
import { OrderSyncingComponent } from '@shared/components/popovers/order-syncing/order-syncing.component';
import { ToastService } from '@shared/services/toast/toast.service';
import { OrderInfoSidebarService } from './services/order-info-sidebar.service';
import { mapHandPricingDetails } from '@product-detail/utils/cpr.util';
import { OrderPunchoutService } from './services/order-punchout.service';
import { DownloadOrdersEvent } from './models/download-orders-event';
import { DownloadFileTypes } from '@shared/constants/order-download-file';
import { DateService } from '@shared/services/date.service';
import { cartQuantityUpdateTracking } from './tracking/analytics/factories/update-quantity-analytics.factory';
import { SEARCH_PAGES_ENUM } from '@shared/constants/search-pages.enum';
import { OrderInsightsService } from './services/order-insights.service';
import { PUNCHOUT_PO_PENDING } from '@shared/constants/order-punchout';
import { QuantityUpdate } from '@shared/models/quantity-update';
import { PdpLnkSrc } from '@shared/constants/pdp-lnksrc.enum';
import { CalendarV2ModalComponent } from '@shared/components/calendar-v2-modal/calendar-v2-modal.component';
import dayjs from 'dayjs';
import { isProntoDay } from '@shared/helpers/pronto.helpers';
import { CPRModalComponent } from '@shared/components/cpr-modal/cpr-modal.component';
import { OrderAnalyticsMessages } from './tracking/analytics/order-analytics.constants';
import { MerchandisingProductViewModel } from '@shared/models/merchandising-product-view.model';
import { ClaimsUserType } from './constants/claims-user-type';
import { CurrencyService } from '@shared/services/currency.service';
import { CustomerStoreService } from '@app/ngrx-customer/services';
import { Customer } from '@usf/customer-types';
import { ProductDetailViewModel } from '@product-detail/models/product-detail-view-model';
import { Unsubscribe } from '@coveo/headless';
import {
  buildRecommendations,
  Recommendations,
} from '@coveo/headless/commerce';
import { environment } from 'src/environments/environment';
import { CoveoService } from '@shared/services/coveo/coveo.service';
import { NewSearchService } from '@app/new-search/services/new-search.service';
@Component({
  selector: 'app-order',
  templateUrl: './order.page.html',
  styleUrls: ['./order.page.scss'],
})
export class OrderPage implements OnInit, ViewWillEnter {
  @ViewChild('poNumberRef') poNumberRef: ElementRef<HTMLAnchorElement>;
  @ViewChild(IonContent, { static: false }) content: IonContent;
  @Input() popoverOptions: PopoverOptions;

  FREQUENTLY_BOUGHT_TOGETHER_FEATURE_NAME =
    OrderAnalyticsMessages.frequentlyBoughtTogetherFeatureName;

  poNumber: string;
  poRegex = new RegExp('^[-0-9a-zA-Z ]{1,20}$');
  message = 'Please wait while we load your order';
  customerStateSubscription$: Subscription;
  cartSyncSubscription$: Subscription;
  orderViewModelSub: Subscription;
  orderViewModelSellerPriceSub: Subscription;
  orderProductCardServceSub: Subscription;
  breadcrumbs: Crumb[];
  deliveryDaysSub: Subscription;
  dyfProductNumbersSub: Subscription;
  frequentlyBoughtTogetherLoadUqeSub: Subscription;
  ordersAdded: boolean;
  eligibleDeliveryDays: number[];
  nextDeliveryInfo: NextDeliveryDetailState;
  nextDeliveryInfo$: Subscription;
  vm: OrderViewModel;
  totalPrice$: Observable<number>;
  orderWeight$: Observable<number>;
  orderViewModel$: Observable<OrderViewModel>;
  sellerPriceDetails$: Observable<SellerPriceDetails>;
  collapsedShipments = new Set();
  totalDollars: number;
  confirmModalOpen = false;
  sellerPrice: Map<number, SellerPriceDetails>;
  selectedCustomer: SelectedCustomerState;
  isDesktop: boolean;
  isMobile: boolean;
  dyfFeatureFlagName = FEATURES.split_global_dyf_products.name;
  tomorrow: Date;
  inspiredPicksFeatureFlagName =
    FEATURES.split_global_inspired_pick_products.name;
  inspiredPicksProductNumbersSub: Subscription;
  merchZoneFeatureFlagName = FEATURES.split_global_review_order_merch_zone.name;
  dateChange: boolean;
  shipmentTypeEnum = ShipmentTypeEnum;
  isShipmentTypeVendor: boolean;
  isSubmitting: boolean;
  checkedForValidRequestedDeliveryDate: boolean; //check for valid delivery date every time user navigates to order page
  customerRequirePO$: Observable<any>;
  isCustomerRequirePO = false;
  isPODisabledForPunchOut = false;
  isOrderMinimumModalOpen = false;
  spinnerSubject = new BehaviorSubject<boolean>(true);
  orderMinVm: OrderMinimumViewModel;
  isOnMinimumOrderMetFlag: boolean;
  isCommonCPREnabled: boolean;
  isSyncing = false;
  editingVSOrder = false;
  userAccessRole$: Observable<boolean> = of(false);
  isTransferLoadingModalOpen = false;
  downloadCartModalIsOpen$ = new BehaviorSubject<boolean>(false);
  addlInstructionsModalIsOpen$ = new BehaviorSubject<boolean>(false);
  downloadCartFileTypes = [DownloadFileTypes.CSV];
  bannerStyle = 'my-deals-order';
  dealsUrl = ['/deals'];
  frequentlyBoughtTogetherLnkSrc = PdpLnkSrc.frequentlyBoughtTogether;
  orderStatusEnum = OrderStatus;
  orderDeliveryInstFeatureFlagName =
    FEATURES.split_global_order_delivery_instructions.name;
  downloadPrintActionSheetIsOpen$ = new BehaviorSubject<boolean>(false);
  downloadPrintButtons = [
    {
      text: this.translate.instant('i18n.orderPage.download'),
      handler: () => {
        this.showDownloadCartModal(true);
      },
    },
    {
      text: this.translate.instant('i18n.orderPage.print'),
      handler: () => {
        this.printOrder();
      },
    },

    {
      text: this.translate.instant('i18n.common.cancel'),
      role: 'cancel',
      cssClass: 'cancel-button',
    },
  ];

  MAX_MERCH_ZONE = 4;
  searchKey = '';
  prontoDaysSub: Subscription;
  prontoDeliveryDays: number[];
  checkedForValidProntoDeliveryDate: boolean;
  origOrderItems: OrderItem[];
  directAllowReturnsFlagName = FEATURES.split_global_direct_allow_returns.name;
  hasAddedInstructions = false;
  currentCustomer: Customer;
  orderMinVmSub: Subscription;

  commerceEngineSubscription$: Subscription;
  recommendationsController: Recommendations;
  recommendationsControllerUnsubscribe: Unsubscribe;

  constructor(
    readonly panAppState: PanAppState,
    readonly orderService: OrderService,
    private popoverHelperService: PopoverHelperService,
    protected orderItemStoreService: OrderItemStoreService,
    private myOrderStoreService: MyOrdersStoreService,
    private selectSellerPricingService: SelectPricingService,
    public popoverController: PopoverController,
    public modalController: ModalController,
    public actionSheetController: ActionSheetController,
    public orderItemService: OrderItemService,
    private deliveryStoreService: DeliveryStoreService,
    readonly platformService: PlatformService,
    private router: Router,
    private translate: TranslateService,
    private navigationHelper: NavigationHelperService,
    private homeService: HomeService,
    private modalService: ModalService,
    private orderTracingService: OrderTracingService,
    private orderProductCardService: OrderProductCardService,
    readonly searchService: SearchService,
    readonly orderAnalyticsService: OrderAnalyticsService,
    private toastService: ToastService,
    private orderInfoSidebarService: OrderInfoSidebarService,
    private orderPunchoutService: OrderPunchoutService,
    private orderInsightsService: OrderInsightsService,
    private customerStoreService: CustomerStoreService,
  ) {}

  ngOnInit() {
    this.isDesktop =
      this.platformService.platformType ===
      this.platformService.platformEnum.desktop;
    this.isMobile =
      this.platformService.platformType ===
      this.platformService.platformEnum.mobile;
    this.isPORequire();
    combineLatest([
      this.panAppState.feature$([FEATURES.split_global_min_order_met.name]),
      this.panAppState.feature$([
        FEATURES.split_global_cpr_override_common_component.name,
      ]),
    ])
      .pipe(take(1))
      .subscribe(([minOrderMetFlag, cprOverrideFlag]) => {
        this.isOnMinimumOrderMetFlag = minOrderMetFlag;
        this.isCommonCPREnabled = cprOverrideFlag;
        this.downloadPrintButtons.push({
          text: this.translate.instant('i18n.orderPage.share'),
          handler: () => {
            this.shareOrder();
          },
        });
      });
    this.tomorrow = moment().add(1, 'days').toDate();
  }

  orderItemSearch() {
    this.orderService.orderItemSearch(this.searchKey);
  }

  clearReviewOrderCartSearch() {
    this.searchKey = '';
    this.orderService.clearReviewOrderCartSearch();
  }

  ionViewWillEnter() {
    this.dateChange = false;
    this.orderInfoSidebarService
      .orderUserAccessStatus()
      .pipe(take(1))
      .subscribe(disableSubmit => {
        if (disableSubmit) {
          this.toastService.presentToast(
            this.orderInfoSidebarService.orderUserStatusMessage(),
          );
        }
      });

    this.customerStateSubscription$ = this.homeService
      .loadSelectedCustomerState$()
      ?.subscribe(cust => {
        this.selectedCustomer = cust;
      });
    this.cartSyncSubscription$ = this.orderService
      .isCartSyncingIpOrder$()
      .subscribe(isSyncing => {
        this.isSyncing = isSyncing;
      });

    this.ordersAdded = false;
    this.checkedForValidRequestedDeliveryDate = false;
    this.isSubmitting = false;
    this.isPORequire();
    // Show the spinner when page loads
    this.spinnerSubject.next(true);

    this.orderProductCardServceSub = this.orderProductCardService
      .getHandPricingDetails$()
      .subscribe(handPricingDetails => {
        if (handPricingDetails) {
          this.presentHandPricingModal(handPricingDetails);
        }
      });

    const orderVMFiltered = this.orderService.loadOrderViewModel$().pipe(
      filter(view => !!view),
      tap(vm => {
        this.editingVSOrder =
          vm?.orderHeader?.orderStatus !== OrderStatus.IN_PROGRESS &&
          vm?.orderHeader?.orderType === 'VS';
        this.poNumber = vm.orderHeader?.purchaseOrderNumber; //do-not remove this ngModel field
        if (!this.ordersAdded) {
          if (vm.orderHeader?.orderStatus === OrderStatus.IN_PROGRESS) {
            this.orderService.removeEmptyOrderItems();
          }
          this.ordersAdded = true;
        }
        //if requestedDeliveryDate is in the past or null, update it to nextDeliveryInfo.deliveryDate
        if (
          !this.checkedForValidRequestedDeliveryDate &&
          vm.orderHeader?.orderStatus === OrderStatus.IN_PROGRESS
        ) {
          let orderItems = this.getOrderItems(
            vm.orderItemState?.ids,
            vm.orderItemState?.entities,
          );

          const orderId =
            `${vm.orderHeader?.customerNumber}:` +
            `${vm.orderHeader?.departmentNumber}:` +
            `${vm.orderHeader?.orderId}`;

          this.myOrderStoreService
            .getOrderWithId(orderId)
            .pipe(take(1))
            .subscribe(order => {
              this.origOrderItems = order?.orderItems;
              let reqDeliveryDate = new Date(
                order?.orderHeader?.requestedDeliveryDate,
              );
              let offset = reqDeliveryDate.getTimezoneOffset();
              reqDeliveryDate.setHours(
                reqDeliveryDate.getHours() + offset / 60,
              );
              let requestedDeliveryDateOffset = new Date(reqDeliveryDate);
              if (
                requestedDeliveryDateOffset < new Date() ||
                !order?.orderHeader?.requestedDeliveryDate
              ) {
                this.orderService.validateNextDeliveryDate(
                  vm.orderHeader,
                  orderItems,
                );
              }
              this.checkedForValidProntoDeliveryDate = this.isProntoDay(
                reqDeliveryDate?.toString(),
              );
            });
          this.checkedForValidRequestedDeliveryDate = true;
        } else if (
          !this.checkedForValidRequestedDeliveryDate &&
          (vm?.orderHeader?.orderStatus === OrderStatus.SUBMITTED ||
            vm?.orderHeader?.orderStatus ===
              OrderStatus.SUBMITTED_CREDIT_HOLD ||
            vm?.orderHeader?.orderStatus ===
              OrderStatus.SUBMITTED_WITH_EXCEPTIONS)
        ) {
          this.checkedForValidRequestedDeliveryDate = true;
        }

        if (!!vm.orderHeader?.orderStatus && !this.orderMinVm) {
          this.orderMinVmSub = this.orderService
            .loadOrderMinimumViewModel$()
            .pipe(debounceTime(250))
            .subscribe(vm => {
              this.orderMinVm = vm;
            });
        }
      }),
    );

    combineLatest([
      this.orderService.isProductStateReady$(),
      this.orderService.isPricingForProductsReady$(),
      this.orderService.isCartLoading$(),
      this.panAppState.customer$,
    ])
      .pipe(
        take(1),
        map(
          ([productsReady, pricingReady, cartLoading, customer]) =>
            productsReady &&
            pricingReady &&
            !cartLoading &&
            customer?.customerNumber === this.vm?.orderHeader?.customerNumber &&
            customer?.departmentNumber?.toString() ===
              this.vm?.orderHeader?.departmentNumber?.toString() &&
            customer.divisionNumber === this.vm?.orderHeader?.divisionNumber,
        ),
      )
      .subscribe((ready: boolean) => {
        if (ready || (!ready && !this.vm?.orderHeader?.error)) {
          this.spinnerSubject.next(false);
        }
      });
    this.customerStoreService
      .loadSelectedCustomer$()
      .pipe(take(1))
      .subscribe(customer => {
        this.currentCustomer = customer;
      });

    // if dyfNUmbers length > 1 wait for the dyf products to be loaded, if not don't wait.
    combineLatest([
      this.orderService.getDyfProductNumbers$(),
      this.orderService.getInspiredPickProductNumbers$(),
      this.orderService.loadOrderViewModel$(),
      this.orderService.getTopSellersProductNumbers$(),
    ])
      .pipe(
        filter(
          ([dyf, picks, vm, topSellers]) =>
            !!vm &&
            !!dyf &&
            !!picks &&
            ((!!dyf.length &&
              (ProductsLoadedService.check(dyf, vm.dyfProducts) ||
                ProductsLoadedService.areMissingDyfProductInfosOnOrder(
                  dyf,
                  vm.dyfProducts,
                  vm.orderItemState.ids as number[],
                ))) ||
              !dyf.length) &&
            ((!!picks.length &&
              ProductsLoadedService.check(
                picks,
                vm.inspiredPickProducts?.products,
              )) ||
              !picks.length) &&
            ((!!topSellers.length &&
              ProductsLoadedService.check(topSellers, vm.merchZoneProducts)) ||
              !topSellers.length),
        ),
        filter(([dyf, picks, vm, topSellers]) =>
          this.orderService.substitutesLoaded(vm),
        ),
        take(1),
      )
      .subscribe(([dyf, picks, vm, topSellers]) => {
        if (vm.orderHeader?.orderId) {
          this.orderService.checkOrderStatus(vm.orderHeader.orderId);
        }
        this.orderService.trackOrderPageLoad(
          vm?.orderHeader,
          vm?.orderItemState,
          vm?.dyfProducts,
          vm?.shipments,
          vm?.inspiredPickProducts,
          vm?.merchZoneProducts,
        );
      });

    orderVMFiltered
      .pipe(
        filter(
          vm =>
            vm.orderItemState?.ids?.length > 0 &&
            vm.online &&
            (vm.orderItemState.ids as number[]).every(
              item => !!vm?.productsMap?.get(item),
            ),
        ),
        take(1),
      )
      .subscribe(vm => {
        const orderItems = this.getOrderItems(
          vm?.orderItemState?.ids,
          vm?.orderItemState?.entities,
        );
        this.orderService.loadUnusualQuantities(vm?.uqeProductNumbers);
        this.orderService.getDirectShippingSavings(
          {
            orderHeader: vm?.orderHeader,
            orderItems: orderItems,
          },
          undefined,
          true,
        );
      });

    this.frequentlyBoughtTogetherLoadUqeSub = orderVMFiltered
      .pipe(
        map(vm => vm?.frequentlyBoughtTogetherProducts?.products),
        filter(products => products?.length > 0),
        take(1),
      )
      .subscribe(products => {
        const uqeProductNumbers: UqeProductNumber[] = products.map(product => ({
          productNumber: product.productNumber,
          pimGroupId: Number(product?.summary?.categoryCode),
        }));
        this.orderService.loadUnusualQuantities(uqeProductNumbers);
      });

    this.orderViewModelSub = orderVMFiltered.subscribe(
      (viewModel: OrderViewModel) => {
        this.vm = viewModel;
        this.isShipmentTypeVendor = this.vm.shipments.some(object => {
          return object.shipmentType === ShipmentTypeEnum.vendor;
        });
        this.totalDollars =
          viewModel?.shipments[0]?.shipmentTallies?.totalPrice;
        this.sellerPrice = viewModel.sellerPrice as Map<
          number,
          SellerPriceDetails
        >;

        if (
          viewModel?.orderHeader?.error?.message &&
          typeof viewModel?.orderHeader?.error?.message === 'string' &&
          viewModel?.orderHeader?.error?.message.includes('submitIpOrder')
        ) {
          this.popoverHelperService.presentPopover(
            this.popoverController,
            WarningPopoverComponent,
            this.translate.instant('i18n.orderPage.submitOrderFailErrorMsg'),
          );
        }
      },
    );

    this.orderViewModelSellerPriceSub = orderVMFiltered
      .pipe(
        filter(
          vm =>
            vm.claimsUserType === ClaimsUserType.CORP_AD &&
            vm.orderItemState?.ids?.length > 0 &&
            vm.online,
        ),
        distinctUntilChanged(
          (previous: [], current: []) => {
            const previousSet = new Set(previous);
            const currentSet = new Set(current);
            return (
              previousSet.size === currentSet.size &&
              [...previousSet].every(value => currentSet.has(value))
            );
          },
          vm => vm.allProductIds,
        ),
      )
      .subscribe(viewModel => {
        this.selectSellerPricingService.getSellerPrices(
          viewModel.allProductIds,
        );
      });

    this.deliveryDaysSub = this.orderService
      .selectedCustomerDeliveryDays()
      .subscribe(selectedDays => {
        this.eligibleDeliveryDays = selectedDays;
      });

    this.prontoDaysSub = this.orderService
      .selectedCustomerProntoDeliveryDays()
      .subscribe(selectedDays => {
        this.prontoDeliveryDays = selectedDays;
        this.checkedForValidProntoDeliveryDate = this.isProntoDay(
          this.vm?.orderHeader?.requestedDeliveryDate?.toString(),
        );
      });

    this.panAppState.setPageTitle(
      this.translate.instant(PageTitle.reviewOrder),
    );

    this.breadcrumbs = [
      {
        label: 'i18n.breadcrumbs.home',
        url: '/desktop/home',
        active: false,
      },
      {
        label: 'i18n.breadcrumbs.myOrders',
        url: '/desktop/my-orders-view',
        active: false,
      },
      {
        label: 'i18n.breadcrumbs.reviewOrder',
        url: '',
        active: true,
      },
    ];

    this.nextDeliveryInfo$ = this.deliveryStoreService
      .getNextDeliveryInformation()
      .subscribe(resp => (this.nextDeliveryInfo = resp));

    this.totalPrice$ = this.orderService.orderTotalPrice();
    this.userAccessRole$ = this.orderInfoSidebarService.orderUserAccessStatus();
    this.orderViewModel$ = this.orderService.loadOrderViewModel$();
    this.orderWeight$ = this.orderViewModel$.pipe(
      map(vm => vm.orderTallies.orderWeight),
    );
  }

  ionViewDidEnter(): void {
    this.isPORequire();
    this.orderService
      .loadOrderViewModel$()
      .pipe(debounceTime(250), take(1))
      .subscribe(vm => {
        const totalCartPrice = vm?.shipments[0]?.shipmentTallies?.totalPrice;
        this.orderTracingService.traceOrderPageLoad(
          vm?.orderHeader?.orderId,
          vm?.orderHeader?.origOrderId,
          vm?.orderHeader?.origSplitOrderId,
          Math.round(totalCartPrice * 100) / 100,
          this.getOrderItems(
            this.vm?.orderItemState?.ids,
            this.vm?.orderItemState?.entities,
          ),
          vm?.orderHeader?.requestedDeliveryDate,
          vm?.orderHeader?.confirmedDeliveryDate,
          vm?.orderHeader?.tandemOrderNumber,
        );
      });
  }

  ionViewDidLeave() {
    if (
      !!this.vm &&
      this.vm.orderHeader.orderStatus === OrderStatus.IN_PROGRESS
    ) {
      // Remove all existing cart order items
      this.orderService.removeEmptyOrderItems();
    }

    if (this.customerStateSubscription$) {
      this.customerStateSubscription$.unsubscribe();
    }
    if (this.orderProductCardServceSub) {
      this.orderProductCardServceSub.unsubscribe();
    }
    if (this.orderViewModelSub) {
      this.orderViewModelSub.unsubscribe();
    }
    if (this.orderViewModelSellerPriceSub) {
      this.orderViewModelSellerPriceSub.unsubscribe();
    }
    if (this.deliveryDaysSub) {
      this.deliveryDaysSub.unsubscribe();
    }
    if (this.nextDeliveryInfo$) {
      this.nextDeliveryInfo$.unsubscribe();
    }
    if (this.cartSyncSubscription$) {
      this.cartSyncSubscription$.unsubscribe();
    }
    if (this.frequentlyBoughtTogetherLoadUqeSub) {
      this.frequentlyBoughtTogetherLoadUqeSub.unsubscribe();
    }
    if (this.prontoDaysSub) {
      this.prontoDaysSub.unsubscribe();
    }
    if (this.orderMinVmSub) {
      this.orderMinVmSub.unsubscribe();
    }

    this.orderProductCardService.resetHandPricingDetails();
    this.clearReviewOrderCartSearch();

    this.commerceEngineSubscription$?.unsubscribe();
    if (this.recommendationsControllerUnsubscribe) {
      this.recommendationsControllerUnsubscribe();
    }
  }

  deleteOrder = async (resp: any) => {
    if (resp?.data?.dispatchAction) {
      const currentOrderHeader: OrderHeader = this.vm.orderHeader;
      const currentOrderItems: OrderItem[] = this.getOrderItems(
        this.vm.orderItemState?.ids,
        this.vm.orderItemState.entities,
      );

      this.orderService
        .getDyfProductNumbers$()
        .pipe(take(1))
        .subscribe((dyfProductNumbers: number[]) => {
          const updatedOrderItems = currentOrderItems.map(item => {
            //Reset dyf products qty if  IP order cancelled after product added
            if (dyfProductNumbers.includes(item.productNumber)) {
              const modifiedDyfItem = {
                ...item,
                unitsOrdered: {
                  currentValue: 0,
                  previousValue: null,
                  loading: false,
                  error: null,
                },
                eachesOrdered: {
                  currentValue: 0,
                  previousValue: null,
                  loading: false,
                  error: null,
                },
              };
              return modifiedDyfItem;
            }
            return item; //the original item if there's no dyf match
          });
          this.orderService.deleteOrder(
            currentOrderHeader,
            updatedOrderItems, // Pass the modified order items
          );
        });
    }
  };

  deleteOrderModal = async () => {
    if (!this.confirmModalOpen) {
      if (this.platformService.isTouch.value) {
        this.modalService.setModalOptions(
          true,
          SimpleActionModalComponent,
          {
            title: this.translate.instant('i18n.orderPage.confirm'),
            content: this.translate.instant('i18n.orderPage.confirmSubtitle'),
            note: this.translate.instant('i18n.orderPage.confirmDescription'),
            exitButtonForTablet: true,
            exitButtonForMobile: true,
          },
          'simple-action-modal',
        );
      } else {
        this.modalService.setModalOptions(
          false,
          SimpleActionModalComponent,
          {
            title: this.translate.instant('i18n.orderPage.confirm'),
            content: this.translate.instant('i18n.orderPage.confirmSubtitle'),
            note: this.translate.instant('i18n.orderPage.confirmDescription'),
            exitButtonForTablet: true,
            exitButtonForMobile: true,
          },
          'simple-action-modal',
        );
      }

      const modal = await this.modalController.create(
        this.modalService.modalOptions,
      );

      modal.onDidDismiss().then((resp: any) => {
        if (!this.vm?.orderHeader?.tandemOrderNumber) {
          this.deleteOrder(resp);
        } else if (resp?.data?.dispatchAction) {
          this.orderService.cancelSubmittedOrder({
            orderHeader: this.vm?.orderHeader,
            orderItems: this.getOrderItems(
              this.vm.orderItemState?.ids,
              this.vm?.orderItemState?.entities,
            ),
          });
        }
        this.confirmModalOpen = false;
      });
      this.confirmModalOpen = true;
      return await modal.present();
    }
  };

  submitOrder(event: any) {
    setTimeout(() => {
      this.handleOrderSubmission();
    }, 1000);
  }

  scrollToElement(elementId) {
    const id = document.getElementById(elementId);
    const offset = id.getBoundingClientRect();

    this.platformService.getPlatformType() === 'desktop'
      ? id.scrollIntoView({
          behavior: 'smooth',
        })
      : this.content.scrollToPoint(0, offset.top - 75, 500);
    this.orderAnalyticsService.trackJumpToDirectClick();
  }

  validateOrderMinimumMet() {
    if (
      !!this.orderMinVm &&
      !this.orderMinVm?.hasOnlyVSItems &&
      (this.vm?.orderHeader?.orderStatus === OrderStatus.IN_PROGRESS ||
        this.vm?.orderHeader?.orderStatus === OrderStatus.SUBMITTED ||
        this.vm?.orderHeader?.orderStatus ===
          OrderStatus.SUBMITTED_CREDIT_HOLD ||
        this.vm?.orderHeader?.orderStatus ===
          OrderStatus.SUBMITTED_WITH_EXCEPTIONS) &&
      (this.orderMinVm.remainingDollars > 0 ||
        this.orderMinVm.remainingCases > 0)
    ) {
      if (
        this.isProntoDay(
          this.vm?.orderHeader?.requestedDeliveryDate?.toString(),
        )
      ) {
        //Pronto Day: pronto delivery dates (green in calendar)
        this.openOrderMinimumModalForPronto();
      } else {
        this.openOrderMinimumModalForBroadline();
      }
    } else {
      this.finishOrderSubmission();
    }
  }

  handleOrderSubmission() {
    const isSubmittedOrder = this.vm.orderHeader?.tandemOrderNumber > 0;

    const reqDeliveryDate = new Date(
      this.vm.orderHeader?.requestedDeliveryDate,
    );
    const offset = reqDeliveryDate.getTimezoneOffset();
    reqDeliveryDate.setHours(reqDeliveryDate.getHours() + offset / 60);
    const requestedDeliveryDateOffset = new Date(reqDeliveryDate);

    const confDeliveryDate = new Date(
      this.vm.orderHeader?.confirmedDeliveryDate,
    );
    const confOffset = confDeliveryDate.getTimezoneOffset();
    confDeliveryDate.setHours(confDeliveryDate.getHours() + confOffset / 60);
    const confirmedDeliveryDateOffset = new Date(confDeliveryDate);

    if (!this.vm.online) {
      this.popoverHelperService.presentPopover(
        this.popoverController,
        WarningPopoverComponent,
        this.translate.instant('i18n.orderPage.offlineSubmitErrorMsg'),
      );
    } else if (
      this.nextDeliveryInfo &&
      !this.nextDeliveryInfo.creditIndicator
    ) {
      this.popoverHelperService.presentPopover(
        this.popoverController,
        WarningPopoverComponent,
        this.translate.instant('i18n.orderPage.invalidDateSubmitErrorMsg'),
      );
    } else if (
      (!isSubmittedOrder && requestedDeliveryDateOffset < new Date()) ||
      (isSubmittedOrder && confirmedDeliveryDateOffset < new Date())
    ) {
      this.popoverHelperService.presentPopover(
        this.popoverController,
        WarningPopoverWithTitleComponent,
        this.translate.instant('i18n.orderPage.requestedDateInPastErrorMsg'),
        this.translate.instant('i18n.orderPage.requestedDateInPastErrorTitle'),
      );
    } else if (this.isSyncing) {
      this.openSyncingModal();
    } else {
      this.isSubmitting = true;
      combineLatest([
        this.orderInsightsService.orderItemsNotOnRecentlyPurchased$(
          this.getOrderItems(
            this.vm.orderItemState?.ids,
            this.vm.orderItemState.entities,
          ).filter(
            item =>
              (item?.eachesOrdered?.currentValue ?? 0) !== 0 ||
              (item?.unitsOrdered?.currentValue ?? 0) !== 0,
          ),
        ),
        this.orderInsightsService.dyfProductsNotOrdered$(),
      ])
        .pipe(take(1))
        .subscribe(
          ([
            newProductsOrderedNotInRecentlyPurchased,
            dyfProductsNotOrdered,
          ]) => {
            if (
              this.orderInsightsService.shouldIncludeInsightsOnOrderSubmission(
                this.vm.orderHeader,
              )
            ) {
              this.vm.orderHeader = {
                ...this.vm.orderHeader,
                newProductsOrderedNotInRecentlyPurchased,
                dyfProductsNotOrdered,
              };
            }
            this.validateOrderMinimumMet();
          },
        );
      this.orderMinVmSub = this.orderService
        .loadOrderMinimumViewModel$()
        .subscribe(vm => {
          this.orderMinVm = vm;
        });
    }
  }

  async openSyncingModal() {
    this.modalService.setModalOptions(
      this.platformService?.isTouch.value,
      OrderSyncingComponent,
      {},
      'simple-action-modal',
    );

    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    return await modal.present();
  }

  finishOrderSubmission() {
    let newOrder;
    const reqDate = new Date(
      this.vm.orderHeader.requestedDeliveryDate,
    ).toISOString();
    const confDate = new Date(
      this.vm.orderHeader.confirmedDeliveryDate,
    ).toISOString();

    const minOrderDollars =
      (this.orderMinVm?.previousRemainingDollars || 0) > 0
        ? this.orderMinVm?.previousRemainingDollars.toFixed(2)
        : 0;
    const minOrderCases =
      (this.orderMinVm?.previousRemainingCases || 0) > 0
        ? this.orderMinVm?.previousRemainingCases
        : 0;
    const minOrderDollarsDiff =
      (this.orderMinVm?.remainingDollars || 0) > 0
        ? this.orderMinVm?.remainingDollars.toFixed(2)
        : 0;
    const minOrderCasesDiff =
      (this.orderMinVm?.remainingCases || 0) > 0
        ? this.orderMinVm?.remainingCases
        : 0;
    if (
      this.vm.orderHeader.orderStatus === OrderStatus.SUBMITTED ||
      this.vm.orderHeader.orderStatus ===
        OrderStatus.SUBMITTED_WITH_EXCEPTIONS ||
      this.vm.orderHeader.orderStatus === OrderStatus.SUBMITTED_CREDIT_HOLD
    ) {
      newOrder = {
        ...this.vm.orderHeader,
        requestedDeliveryDate: this.dateChange ? reqDate : confDate,
        confirmedDeliveryDate: confDate,
        totalDollars: this.totalDollars,
        updateDtm: new Date().toISOString(),
        addDtm: new Date().toISOString(),
        minOrderDollars,
        minOrderCases,
        minOrderDollarsDiff,
        minOrderCasesDiff,
      };
    } else {
      newOrder = {
        ...this.vm.orderHeader,
        requestedDeliveryDate: reqDate,
        confirmedDeliveryDate: reqDate,
        minOrderDollars,
        minOrderCases,
        minOrderDollarsDiff,
        minOrderCasesDiff,
      };
    }
    if (newOrder.purchaseOrderNumber === PUNCHOUT_PO_PENDING) {
      newOrder.purchaseOrderNumber = undefined;
    }
    const orderItems: OrderItem[] = this.getOrderItems(
      this.vm.orderItemState?.ids,
      this.vm.orderItemState?.entities,
    );
    const numberOfShipments = this.vm.orderTallies?.totalShipments || 0;
    if (this.vm.orderItemState.tallies?.totalItems > 0) {
      const trackingData: Tracking = {
        analytics: {
          data: {
            order: {
              numberOfShipments,
              id: this.vm.orderHeader.orderId,
            },
          },
        },
      };
      //sorting order by sequence number in ascending-order before submission
      orderItems.sort((a, b) => a.sequence - b.sequence);
      if (this.vm.partnerAutotransfer) {
        sessionStorage.setItem('autoTransferOrder', 'true');
        this.orderPunchoutService.createSpinnerWithMessageModal();
      }
      if (this.vm?.isPartnerModifyingOrder) {
        sessionStorage.setItem('isPartnerModifyingOrder', 'true');
      }
      this.orderTracingService.trackSubmitOrderStart({
        ...this.vm?.orderHeader,
        numberOfShipments,
      });
      this.orderService.submitOrder(newOrder, orderItems, trackingData);
    } else {
      this.isSubmitting = false;
      this.popoverHelperService.presentPopover(
        this.popoverController,
        WarningPopoverComponent,
        this.translate.instant('i18n.orderPage.noProductSubmitErrorMsg'),
      );
    }
  }

  async presentChangePOPopover() {
    this.modalService.setModalOptions(
      this.platformService?.isTouch.value,
      PoPopoverComponent,
      {},
      'modal-desktop-size-sm',
    );

    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    return await modal.present();
  }

  async openSubmitModalMobile(event: any) {
    if (this.vm.orderItemState.tallies?.totalItems > 0) {
      this.modalService.setModalOptions(true, ModalWithNavComponent, {
        rootPage: SubmitModalMobileComponent,
        rootPageParams: {
          vm: this.vm,
          isDiscardEdits: false,
          prontoDeliveryDays: this.prontoDeliveryDays,
          selectedCustomer: this.selectedCustomer,
          nextDeliveryInfo: this.nextDeliveryInfo,
          isCustomerRequirePO: this.isCustomerRequirePO,
          isPODisabledForPunchOut: this.isPODisabledForPunchOut,
        },
      });
      const modal = await this.modalController.create(
        this.modalService.modalOptions,
      );
      modal.onDidDismiss().then((resp: any) => {
        if (resp?.data) {
          if (resp.data.submitOrder) {
            this.submitOrder(event);
          } else if (resp.data.cancelOrder) {
            this.deleteOrderModal();
          }
        }
      });
      return await modal.present();
    } else if (
      this.nextDeliveryInfo &&
      !this.nextDeliveryInfo.creditIndicator
    ) {
      this.popoverHelperService.presentPopover(
        this.popoverController,
        WarningPopoverComponent,
        this.translate.instant('i18n.orderPage.invalidDateSubmitErrorMsg'),
      );
    } else {
      this.popoverHelperService.presentPopover(
        this.popoverController,
        WarningPopoverComponent,
        this.translate.instant('i18n.orderPage.noProductSubmitErrorMsg'),
      );
    }
  }

  async openDiscardOrderModalMobile(event: any) {
    this.modalService.setModalOptions(true, ModalWithNavComponent, {
      rootPage: SubmitModalMobileComponent,
      rootPageParams: {
        vm: this.vm,
        prontoDeliveryDays: this.prontoDeliveryDays,
        isDiscardEdits: true,
        selectedCustomer: this.selectedCustomer,
        nextDeliveryInfo: this.nextDeliveryInfo,
        isCustomerRequirePO: this.isCustomerRequirePO,
        isPODisabledForPunchOut: this.isPODisabledForPunchOut,
      },
    });
    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    modal.onDidDismiss().then((resp: any) => {
      if (resp?.data && resp.data.discardEdits) {
        this.orderService.getDirectShippingSavings(
          {
            orderHeader: this.vm?.orderHeader,
            orderItems: this.origOrderItems,
          },
          undefined,
          false,
        );
        this.orderService.resetOrder();
        this.redirectTo(PATHS.MY_ORDERS_VIEW);
      }
    });
    return await modal.present();
  }

  isEligibleDay = (dateString: string) => {
    const today = dayjs().startOf('day').format('YYYY-MM-DD');
    if (today === dateString) {
      return false;
    }
    const date = new Date(dateString);
    const utcDay = date.getUTCDay();
    const isDateEnabled =
      this.eligibleDeliveryDays?.includes(utcDay) &&
      dateString > today &&
      dateString != this.vm.orderHeader.requestedDeliveryDate;
    return isDateEnabled;
  };

  prontoDayStyle = (isoString: string) => {
    if (this.prontoDeliveryDays === undefined) return undefined;
    if (moment().isSameOrBefore(isoString) && this.isEligibleDay(isoString)) {
      if (this.isProntoDay(isoString)) {
        return {
          textColor: 'var(--ion-color-gray-80)',
          backgroundColor: '#F3BA1633',
        };
      } else {
        return {
          textColor: 'var(--ion-color-gray-80)',
          backgroundColor: 'var(--ion-color-primary-green-20)',
        };
      }
    }
    return undefined;
  };
  async presentCalendarPopover(event: any) {
    event?.stopPropagation();

    if (!this.vm.online) {
      this.popoverHelperService.presentPopover(
        this.popoverController,
        WarningPopoverComponent,
        this.translate.instant('i18n.orderPage.offlineDeliveryDateErrorMsg'),
      );
    } else {
      const newOrder: OrderHeader = {
        ...this.vm.orderHeader,
      };
      const currentDate = new Date();

      const minDate = new Date(
        currentDate.getUTCFullYear(),
        currentDate.getUTCMonth(),
        currentDate.getUTCDate() + 1,
      ).toISOString();
      const maxDate = new Date(
        currentDate.getUTCFullYear() + 1,
        currentDate.getUTCMonth(),
        currentDate.getUTCDate(),
      ).toISOString();

      const requestedDateWithoutTimezone = new Date(
        this.vm.orderHeader?.requestedDeliveryDate,
      )
        .toISOString()
        .split('T')[0];

      const confirmedDateWithoutTimezone = new Date(
        this.vm.orderHeader?.confirmedDeliveryDate,
      )
        .toISOString()
        .split('T')[0];

      const lastSelectedDate = !!newOrder.tandemOrderNumber
        ? confirmedDateWithoutTimezone
        : requestedDateWithoutTimezone;

      this.modalService.setModalOptions(
        this.platformService.isTouch.value,
        CalendarV2ModalComponent,
        {
          title: this.translate.instant('i18n.orderPage.changeDeliveryDate'),
          lastSelectedDate: lastSelectedDate,
          minDate,
          maxDate,
          isDateEnabled: this.isEligibleDay,
          showClearButton: false,
          prontoDates: this.prontoDayStyle,
          isProntoAvailable: this.prontoDeliveryDays !== undefined,
        },
        'modal-calendar-desktop-size-lg',
      );
      const modal = await this.modalController.create(
        this.modalService.modalOptions,
      );

      modal.onDidDismiss().then((resp: any) => {
        if (resp && resp.data && resp.data.selectedDate) {
          this.changeDeliveryDate(resp.data.selectedDate);
        }
      });
      return await modal.present();
    }
  }

  async presentHandPricingModal(productDetails: HandPricingProductDetails) {
    if (productDetails.currentDetails?.productNumber) {
      this.orderService.trackHandPricingModalOpen(
        this.vm.orderItemState.entities[
          productDetails.currentDetails.productNumber
        ],
      );
    }
    const sellerPriceDetail =
      this.sellerPrice[productDetails?.currentDetails?.productNumber];
    const altSellerPriceDetail =
      this.sellerPrice[productDetails?.altProductDetails?.productNumber];
    const sellerHandPricingDetail = mapHandPricingDetails(
      sellerPriceDetail,
      productDetails?.currentDetails,
      true,
    );
    const altHandPricingDetail = mapHandPricingDetails(
      altSellerPriceDetail,
      productDetails?.altProductDetails,
      true,
    );
    const product = this.vm.productsMap?.get(
      productDetails?.currentDetails?.productNumber,
    );
    const alternativeProduct = this.vm.productsMap?.get(
      productDetails?.altProductDetails?.productNumber,
    );

    const productTracingData = SharedTrackingService.tracingProductData(
      this.getOrderItems(
        this.vm.orderItemState?.ids,
        this.vm?.orderItemState?.entities,
      ),
      this.vm.productsMap,
    );
    const districtNgpCprInd = sellerPriceDetail?.districtNgpCprInd;
    const isCprModal = districtNgpCprInd === 'Y';
    const isCprSetupInd =
      sellerPriceDetail?.currentPrice?.priceControls?.cprSetupInd === 'Y';
    const priceUom = sellerHandPricingDetail.priceUom;

    if (isCprModal) {
      if (this.isCommonCPREnabled) {
        this.modalService.setModalOptions(
          !!this.platformService.isTouch.value,
          CPRModalComponent,
          {
            product,
            sellerPriceDetail,
            alternativeProduct,
            alternativeSellerPriceDetail: altSellerPriceDetail,
          },
          'modal-desktop-size-xl',
        );
      } else {
        this.modalService.setModalOptions(
          !!this.platformService.isTouch.value,
          HandPricingCPRandNGPComponent,
          {
            sellerPriceDetail,
            altSellerPriceDetail,
            sellerHandPricingDetail,
            altHandPricingDetail,
            isCprSetupInd,
            districtNgpCprInd,
            priceUom,
            orderHeader: this.vm.orderHeader,
            productTracing: productTracingData,
          },
          'modal-desktop-size-xl',
        );
      }
    } else {
      this.modalService.setModalOptions(
        this.platformService.isTouch.value,
        HandPricingPopoverComponent,
        {
          productDetails: productDetails.currentDetails,
          altProductDetails: productDetails.altProductDetails,
          sellerPrice: sellerPriceDetail,
          altSellerPrice: altSellerPriceDetail,
        },
        'modal-desktop-size-lg',
      );
    }

    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    modal.onDidDismiss().then((res: any) => {
      if (!!res.data) {
        this.updateSellerPrice(res.data);
      }
    });
    return await modal.present();
  }

  updateSellerPrice(data) {
    if (!this.vm.online) {
      this.popoverHelperService.presentPopover(
        this.popoverController,
        WarningPopoverComponent,
        this.translate.instant('i18n.orderPage.offlineErrorMsg'),
      );
    } else if (!!data && !!data.productNumber) {
      this.orderService.trackHandPricingChange(
        this.vm.orderItemState.entities[data.productNumber],
      );
      this.orderItemService.updateOrderItemSellerPrice(
        data.productNumber,
        data.unitPrice || 0,
        data.unitPriceOverride || 0,
      );
    }
  }

  changeDeliveryDate(newDate: Date) {
    this.dateChange =
      new Date(newDate).toISOString() !==
      new Date(this.vm.orderHeader.confirmedDeliveryDate).toISOString();
    // If the user doesn't change the date, then no need to update the state
    if (!this.dateChange) {
      return;
    }

    const newOrder: OrderHeader = {
      ...this.vm.orderHeader,
      requestedDeliveryDate: new Date(newDate).toISOString(),
      dirtyFlag: true,
    };
    const orderItems: OrderItem[] = [];
    const ids = this.vm.orderItemState?.ids;
    for (const id of ids) {
      if (
        Object.prototype.hasOwnProperty.call(
          this.vm.orderItemState.entities,
          id,
        )
      ) {
        const element = this.vm.orderItemState.entities[id];
        orderItems.push(element);
      }
    }
    this.orderService.updateOrderHeader(newOrder, orderItems, this.dateChange);
    this.checkedForValidProntoDeliveryDate = this.isProntoDay(
      new Date(newDate).toISOString(),
    );
  }

  quantityUpdateHandler(quantityUpdate: any) {
    quantityUpdate = quantityUpdate.quantityUpdate;

    const trackingData = cartQuantityUpdateTracking(quantityUpdate, this.vm);

    this.orderItemService.changeQuantity(
      quantityUpdate.productNumber,
      quantityUpdate.quantity,
      quantityUpdate.isEaches,
      trackingData,
      null,
      true,
    );
  }

  onFrequentlyBoughtTogetherQuantityUpdate(
    quantityUpdate: QuantityUpdate,
    index: number,
    product: MerchandisingProductViewModel,
  ) {
    const trackingData: Tracking = {
      analytics: {
        data: {
          products: [
            {
              addMethod:
                OrderAnalyticsMessages.frequentlyBoughtTogetherFeatureName,
              merchFeatureName:
                OrderAnalyticsMessages.frequentlyBoughtTogetherFeatureName,
              variant: product?.variant,
            },
          ],
          results: {
            resultRank: index + 1,
          },
        },
      },
    };
    this.orderItemService.changeQuantity(
      quantityUpdate.productNumber,
      quantityUpdate.quantity,
      quantityUpdate.isEaches,
      trackingData,
      true,
      true,
    );
  }

  onFrequentlyBoughtTogetherStartObservation() {
    const products = this.vm?.frequentlyBoughtTogetherProducts?.products;
    if (products?.length) {
      this.orderAnalyticsService.trackFrequentlyBoughtTogetherDisplayed(
        products.map(product => ({
          divisionApn: `${product?.summary?.divisionNumber}-${product?.productNumber}`,
          attributes: product?.trackingAttributes,
          merchFeatureView: '1',
          merchFeatureName:
            OrderAnalyticsMessages.frequentlyBoughtTogetherFeatureName,
          variant: product?.variant,
        })),
      );
    }
  }

  onFrequentlyBoughtTogetherView() {
    this.orderAnalyticsService.trackFrequentlyBoughtTogetherView();
  }

  redirectTo(path: string) {
    const url = window.location.pathname;
    this.router.navigate([url.split('/')[1], path]);
  }

  trackByIndex = (index: number, id: any) => index;

  getOrderItems = (ids: any[], entities: {}): OrderItem[] =>
    ids && entities ? ids.map(id => entities[id]) : undefined;

  toggleShipmentProducts(index: number): void {
    if (this.collapsedShipments.has(index)) {
      this.collapsedShipments.delete(index);
    } else {
      this.collapsedShipments.add(index);
    }
  }

  /**
   *
   * @param cartItem displays in a routed/direct group
   * @returns true when the cartItem has quantities and has an altProductNumber
   */
  isSplit(cartItem: CartItem) {
    return (
      this.vm?.alternatives?.has(cartItem.altProductNumber) &&
      (!!cartItem.orderItem.unitsOrdered?.currentValue ||
        !!cartItem.orderItem.eachesOrdered?.currentValue)
    );
  }

  /**
   *
   * @param cartItem displays in a routed/direct group
   * @returns true when the cartItem is in the list of alts
   */
  isAlternative(cartItem: CartItem) {
    return this.vm?.alternatives.has(cartItem?.productState?.productNumber);
  }

  /**
   *
   * @param cartItem displays in a routed/direct group
   * @returns true when the cartItem parent orderItem has quantities
   */
  parentHasQuantity(cartItem: CartItem): boolean {
    let alternative = this.vm?.alternatives.get(
      cartItem.productState.productNumber,
    );
    const parentOrderItem =
      this.vm?.orderItemState.entities[alternative?.parentProductNumber];
    return (
      !!parentOrderItem?.unitsOrdered?.currentValue ||
      !!parentOrderItem?.eachesOrdered?.currentValue
    );
  }

  navigateToDYFPage(resultRank: number, productNumber: number) {
    this.navigationHelper.routeTo(PATHS.DYF);
    this.orderService.trackDidYouForgetClick(
      resultRank + 1,
      this.vm.productsMap.get(productNumber),
    );
    this.orderTracingService.traceDyfBannerClick(
      this.vm.orderHeader.orderId,
      this.vm.orderHeader.tandemOrderNumber,
      resultRank + 1,
      this.vm.productsMap.get(productNumber),
    );
  }

  navigateToDYFPageFromInspiredPicks(
    resultRank: number,
    productNumber: number,
  ) {
    this.navigationHelper.routeTo(PATHS.DYF);
    this.orderService.trackInspiredPicksClick(
      resultRank + 1,
      this.vm.productsMap.get(productNumber),
    );
    this.orderTracingService.traceInspiredPicksBannerClick(
      this.vm?.orderHeader?.orderId,
      this.vm?.orderHeader?.tandemOrderNumber,
      resultRank + 1,
      this.vm?.productsMap?.get(productNumber),
    );
  }

  navigateToDYFPageFromMerchZone(resultRank: number, productNumber: number) {
    this.orderAnalyticsService.trackMerchZoneClick(
      this.vm?.productsMap?.get(productNumber),
      resultRank + 1,
    );
    this.navigationHelper.routeTo(PATHS.DYF);
  }

  searchForSubHandler(productNumber): void {
    const product = this.vm.productsMap.get(productNumber);
    if (product?.summary) {
      this.orderService.navigateToSearchForSubs(product);
    }
  }

  openProductCardEllipsis(ellipsisClickData: EllipsisClickData) {
    const buttons = this.orderProductCardService.constructEllipsisButtons(
      ellipsisClickData,
      SEARCH_PAGES_ENUM.reviewOrder,
    );
    this.orderProductCardService.openProductCardEllipsis(
      buttons,
      ellipsisClickData.isTouch,
      ellipsisClickData.event,
    );
  }

  clickCard(event: ProductClickData) {
    this.orderProductCardService.routeToPDP(event.productNumber);
  }

  async openHelpModal() {
    if (this.platformService.isTouch.value) {
      this.modalService.setModalOptions(true, ModalWithNavComponent, {
        rootPage: HelpModalTouchComponent,
      });
    } else {
      this.modalService.setModalOptions(
        false,
        ModalWithNavComponent,
        {
          rootPage: HelpModalDesktopComponent,
        },
        'modal-desktop-size-xl',
      );
    }

    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    await modal.present();
  }

  /* If requestedDeliveryDate and nextDeliveryDate fails -> returns tomorrow date */
  get requestedDeliveryDate(): string | Date {
    let requestDate;

    if (!!this.nextDeliveryInfo?.deliveryDate) {
      try {
        requestDate = new Date(this.nextDeliveryInfo.deliveryDate);
        if (requestDate.toString() === 'Invalid Date') {
          requestDate = null;
        } else {
          return requestDate;
        }
      } catch (e) {
        requestDate = null;
      }
    }

    if (!!this.vm?.orderHeader?.requestedDeliveryDate) {
      try {
        requestDate = new Date(this.vm.orderHeader.requestedDeliveryDate);
        if (requestDate.toString() === 'Invalid Date') {
          requestDate = null;
        } else {
          return requestDate;
        }
      } catch (e) {
        requestDate = null;
      }
    }

    if (!requestDate) {
      return this.tomorrow;
    }
  }

  goToDirect(event: any) {
    event.stopPropagation();
    event.preventDefault();
    this.orderAnalyticsService.trackBuyMoreAndSaveClick();
    this.searchService.goToDirect();
  }

  async openProductStatus(ellipsisClickData: EllipsisClickData) {
    this.orderProductCardService.showProductStatuses(ellipsisClickData);
  }

  confirmUnusualQuantity(productNumber: number) {
    const orderItem = this.vm?.orderItemState.entities[productNumber];
    const updateOrderItem = {
      ...orderItem,
      quantityAccepted: true,
      submittedQuantityAccepted: this.vm?.orderHeader?.tandemOrderNumber
        ? true
        : false,
    };
    this.orderService.confirmUnusualQuantityAnalitics(orderItem);
    this.orderTracingService.traceConfirmUQEButtonClickedOnOrderPage(
      this.vm?.orderHeader,
      orderItem,
      this.vm?.productsMap,
    );
    this.orderService.updateOrder(
      this.vm?.orderHeader,
      this.getOrderItems(
        this.vm.orderItemState?.ids,
        this.vm.orderItemState?.entities,
      ),
      updateOrderItem,
    );
  }

  updatePoNumber() {
    const newOrder: OrderHeader = {
      ...this.vm?.orderHeader,
      purchaseOrderNumber: this.poNumber,
      dirtyFlag: true,
    };
    const orderItems: OrderItem[] = [];
    for (const key in this.vm?.orderItemState.entities) {
      if (
        Object.prototype.hasOwnProperty.call(
          this.vm?.orderItemState.entities,
          key,
        )
      ) {
        const element = this.vm?.orderItemState.entities[key];
        orderItems.push(element);
      }
    }
    this.orderService.updateOrderHeader(newOrder, orderItems);
  }

  isPORequire(): void {
    this.panAppState.accessToken$
      .pipe(
        filter(token => !!token),
        take(1),
      )
      .subscribe(token => {
        try {
          const decoded = jwtDecode(token);
          if (decoded) {
            this.isCustomerRequirePO =
              decoded['usf-claims']?.requireCustomerPO === 'Y' ? true : false;
            this.isPODisabledForPunchOut =
              decoded['usf-claims']?.punchoutSession ||
              this.vm?.orderHeader?.addOrderSource === 'PO';
          }
        } catch (err) {
          console.error('jwtDecode Failed');
        }
      });
  }

  async openOrderMinimumModalForBroadline() {
    if (!this.isOrderMinimumModalOpen) {
      let dollars;
      let cases;
      if (!!this.orderMinVm && !this.orderMinVm.noPriceCustomer) {
        dollars = this.orderMinVm.remainingDollars?.toFixed(2);
        cases = this.orderMinVm.remainingCases;
      } else {
        dollars = 0;
        cases = 0;
      }

      if (this.platformService.isTouch.value) {
        this.modalService.setModalOptions(
          true,
          SimpleActionModalComponent,
          {
            title: this.translate.instant(
              'i18n.orderPage.remainingMinAmountTit',
            ),
            content: this.translate.instant(
              'i18n.orderPage.remainingMinAmountSub',
            ),
            leftButtonText: this.translate.instant(
              'i18n.orderPage.returnToCart',
            ),
            rightButtonText: this.translate.instant(
              'i18n.orderPage.submitOrder',
            ),
            subtitleClass: 'order-minimum-remaining-sub',
            subtitle: `${dollars > 0 ? '$' + dollars : ''} ${
              dollars > 0 && cases > 0 ? 'and' : ''
            } ${cases > 0 ? cases + ' CS' : ''}`,
          },
          'simple-action-modal',
        );
      } else {
        this.modalService.setModalOptions(
          false,
          SimpleActionModalComponent,
          {
            title: this.translate.instant(
              'i18n.orderPage.remainingMinAmountTit',
            ),
            content: this.translate.instant(
              'i18n.orderPage.remainingMinAmountSub',
            ),
            leftButtonText: this.translate.instant(
              'i18n.orderPage.returnToCart',
            ),
            rightButtonText: this.translate.instant(
              'i18n.orderPage.submitOrder',
            ),
            subtitleClass: 'order-minimum-remaining-sub',
            subtitle: `${dollars > 0 ? '$' + dollars : ''} ${
              dollars > 0 && cases > 0 ? 'and' : ''
            } ${cases > 0 ? cases + ' CS' : ''}`,
            exitButtonForTablet: true,
            exitButtonForMobile: true,
          },
          'simple-action-modal',
        );
      }

      const modal = await this.modalController.create(
        this.modalService.modalOptions,
      );

      //min order view modal
      modal.onDidDismiss().then((resp: any) => {
        if (resp?.data?.dispatchAction === true) {
          this.finishOrderSubmission();
        } else {
          this.isSubmitting = false;
        }
        this.isOrderMinimumModalOpen = false;
      });
      this.isOrderMinimumModalOpen = true;
      return await modal.present();
    }
  }

  async openOrderMinimumModalForPronto() {
    this.modalService.setModalOptions(
      this.platformService?.isTouch.value,
      SimpleActionModalComponent,
      {
        title: this.translate.instant(
          'i18n.orderPage.prontoShippingFeeModal.title',
        ),
        withIcon: true,
        content: this.translate.instant(
          'i18n.orderPage.prontoShippingFeeModal.shippingFeeMsg',
          {
            prontoShippingFee: this.orderMinVm?.prontoShippingFee || 0,
            minCases: this.orderMinVm?.customerMinOrderCases || 0,
          },
        ),
        contentTitle: this.contentTitleForProntoMinFees(),
        exitButtonForTablet: true,
        exitButtonForMobile: true,
        leftButtonText: this.translate.instant('i18n.orderPage.returnToCart'),
        rightButtonText: this.translate.instant('i18n.orderPage.submitOrder'),
      },
      'simple-action-modal',
    );

    const modal = await this.modalController.create(
      this.modalService.modalOptions,
    );
    //min order view modal
    modal.onDidDismiss().then((resp: any) => {
      if (resp?.data?.dispatchAction === true) {
        this.finishOrderSubmission();
      } else {
        this.isSubmitting = false;
      }
      this.isOrderMinimumModalOpen = false;
    });
    this.isOrderMinimumModalOpen = true;
    return await modal.present();
  }

  contentTitleForProntoMinFees(): string {
    let contentCurrency = '';
    let contentCases = '';
    if (
      this.isOnMinimumOrderMetFlag &&
      !!this.vm?.orderHeader &&
      !!this.orderMinVm &&
      !this.orderMinVm?.hasOnlyVSItems &&
      (this.vm?.orderHeader.orderStatus === 'IN_PROGRESS' ||
        this.vm?.orderHeader.orderStatus === 'SUBMITTED' ||
        this.vm?.orderHeader.orderStatus === 'SUBMITTED_CREDIT_HOLD' ||
        this.vm?.orderHeader.orderStatus === 'SUBMITTED_WITH_EXCEPTIONS') &&
      !this.orderMinVm.isMet
    ) {
      if (
        !this.orderMinVm.noPriceCustomer &&
        this.orderMinVm.remainingDollars > 0
      ) {
        contentCurrency = CurrencyService.toUSDString(
          this.orderMinVm.remainingDollars || 0,
        );
      } else if (this.orderMinVm.noPriceCustomer) {
        contentCurrency = CurrencyService.toUSDString(
          this.orderMinVm.customerMinOrderDollars || 0,
        );
      }
      contentCases =
        this.orderMinVm.remainingCases > 0
          ? this.orderMinVm.remainingCases +
            ' ' +
            this.translate.instant('i18n.orderPage.cs')
          : 0 + ' ' + this.translate.instant('i18n.orderPage.cs');
    }

    contentCurrency = contentCurrency ? contentCurrency + ' | ' : '';
    return contentCurrency + contentCases;
  }

  showDownloadPrintActionSheet(isOpen: boolean) {
    this.downloadPrintActionSheetIsOpen$.next(isOpen);
  }

  showDownloadCartModal(isOpen: boolean) {
    this.downloadCartModalIsOpen$.next(isOpen);
  }

  showAddlInstructionsModal(isOpen: boolean) {
    this.addlInstructionsModalIsOpen$.next(isOpen);
  }

  handleAddlInstructionsModalDismiss(event: any) {
    this.showAddlInstructionsModal(false);
    this.hasAddedInstructions = false;
    if (event?.detail?.data?.save) {
      const specialDeliveryInstructions = event?.detail?.data?.instructions;
      const newOrder: OrderHeader = {
        ...this.vm.orderHeader,
        specialDeliveryInstructions,
        dirtyFlag: true,
      };
      const orderItems: OrderItem[] = this.getOrderItems(
        this.vm.orderItemState?.ids,
        this.vm.orderItemState.entities,
      );

      this.orderService.updateOrderHeader(newOrder, orderItems);
    }
  }

  handleDownloadCart(fileInfo: DownloadOrdersEvent) {
    this.orderService.downloadCart(fileInfo);
  }

  formatDownloadFileName() {
    return `Review order detail_${
      this.vm?.orderHeader?.customerNumber
    }_${DateService.formatDateMaintainUTC(new Date(), '')}`;
  }

  cartToOrderArray() {
    return [
      {
        orderHeader: this.vm?.orderHeader,
        orderItems: this.getOrderItems(
          this.vm.orderItemState?.ids,
          this.vm.orderItemState?.entities,
        ),
      },
    ];
  }

  printOrder() {
    this.orderService.printCart();
  }

  shareOrder() {
    this.orderService.shareCart();
  }

  goToTop() {
    this.content.scrollToTop();
  }

  isProntoDay(isoString: string): boolean {
    return isProntoDay(this.prontoDeliveryDays, isoString);
  }

  handleInstructionsAdded(added: boolean) {
    if (added && !this.hasAddedInstructions) {
      this.hasAddedInstructions = true;
      const orderHeader = this.vm?.orderHeader;
      const orderId =
        orderHeader?.origSplitOrderId ||
        orderHeader?.origOrderId ||
        orderHeader?.orderId;
      const products = this.vm?.productsMap;

      const productData = this.vm?.orderItemState?.ids.map(productNumber => {
        return {
          divisionApn: `${orderHeader?.divisionNumber}-${productNumber}`,
          attributes: products.get(productNumber)?.trackingAttributes,
        };
      });

      this.orderAnalyticsService.trackAdditionalDeliveryInstructions(
        productData,
        orderId,
      );
    }
  }
}
